import React from "react";
import { TileableComponent } from "../../app/tileable/TileableComponent";
import { AdminLoginForm } from "./component/AdminLoginForm";
import { Admin2FAForm } from "./component/Admin2FAForm";
import { AdminLoginServiceSingleton, BrowserServiceSingleton } from "src/Context";
import { HttpError } from "../../common/http/model/HttpError";
import "./AdminLoginPage.scss";

type View = "loading" | "login" | "2fa";

const RESEND_COOLDOWN_SECONDS = 60;

interface State {
    view: View;
    error: string | null;
    loading: boolean;
    resendCooldownSeconds: number;
    resendLoading: boolean;
}

/**
 * Admin Login page. Flow: GET /admin/login/me → login or 2FA form → POST /admin/login, POST /admin/login/2fa → redirect to /internal.
 */
export class AdminLoginPage extends React.Component<{}, State> {
    private readonly adminLoginService = AdminLoginServiceSingleton;
    private readonly browserService = BrowserServiceSingleton;
    private resendCooldownTimerId: ReturnType<typeof setInterval> | null = null;

    constructor(props: {}) {
        super(props);
        this.state = {
            view: "loading",
            error: null,
            loading: false,
            resendCooldownSeconds: 0,
            resendLoading: false,
        };
    }

    componentDidMount() {
        this.checkAuthState();
    }

    componentWillUnmount() {
        if (this.resendCooldownTimerId != null) {
            clearInterval(this.resendCooldownTimerId);
        }
    }

    private checkAuthState = () => {
        this.setState({ view: "loading", error: null });
        this.adminLoginService
            .getMe()
            .then((me) => {
                if (me.authenticated) {
                    this.redirectToInternal();
                    return;
                }
                if (me.requires2fa) {
                    this.setState({ view: "2fa", error: null });
                    return;
                }
                this.setState({ view: "login", error: null });
            })
            .catch(() => {
                this.setState({ view: "login", error: null });
            });
    };

    private redirectToInternal = () => {
        this.browserService.redirectTo("/internal");
    };

    private goBack = () => {
        this.browserService.redirectTo("/");
    };

    private handleLoginSubmit = (email: string, password: string) => {
        this.setState({ loading: true, error: null });
        this.adminLoginService
            .login(email, password)
            .then((res) => {
                if (res.success && res.requires2fa) {
                    this.setState({ view: "2fa", loading: false, error: null });
                } else {
                    this.setState({ loading: false });
                }
            })
            .catch((err) => {
                const message =
                    err instanceof HttpError ? err.message : "Sign in failed. Please try again.";
                this.setState({ loading: false, error: message });
            });
    };

    private handle2FASubmit = (code: string) => {
        this.setState({ loading: true, error: null });
        this.adminLoginService
            .submit2fa(code)
            .then((res) => {
                if (res.success) {
                    this.redirectToInternal();
                } else {
                    this.setState({ loading: false });
                }
            })
            .catch((err) => {
                const message =
                    err instanceof HttpError ? err.message : "Verification failed. Please try again.";
                this.setState({ loading: false, error: message });
            });
    };

    private handleResend2fa = () => {
        this.setState({ resendLoading: true, error: null });
        this.adminLoginService
            .resend2fa()
            .then(() => {
                this.setState({
                    resendLoading: false,
                    resendCooldownSeconds: RESEND_COOLDOWN_SECONDS,
                });
                this.startResendCooldown();
            })
            .catch((err) => {
                const message =
                    err instanceof HttpError ? err.message : "Failed to resend code. Try again later.";
                this.setState({ resendLoading: false, error: message });
            });
    };

    private startResendCooldown = () => {
        if (this.resendCooldownTimerId != null) {
            clearInterval(this.resendCooldownTimerId);
        }
        this.resendCooldownTimerId = setInterval(() => {
            this.setState((prev) => {
                const next = prev.resendCooldownSeconds - 1;
                if (next <= 0 && this.resendCooldownTimerId != null) {
                    clearInterval(this.resendCooldownTimerId);
                    this.resendCooldownTimerId = null;
                }
                return { resendCooldownSeconds: next <= 0 ? 0 : next };
            });
        }, 1000);
    };

    private instructions = () => (
        <div className="admin-login-instructions">
            <div className="admin-login-logo" aria-hidden="true">
                <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="40" cy="40" r="36" stroke="rgba(255,255,255,0.9)" strokeWidth="3" fill="rgba(255,255,255,0.15)" />
                    <path d="M40 22a6 6 0 0 0-6 6v4h-4a4 4 0 0 0-4 4v20a4 4 0 0 0 4 4h20a4 4 0 0 0 4-4V36a4 4 0 0 0-4-4h-4v-4a6 6 0 0 0-6-6zm0 4a2 2 0 0 1 2 2v4h-4v-4a2 2 0 0 1 2-2zm-2 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0z" fill="rgba(255,255,255,0.95)" />
                </svg>
            </div>
            <p className="admin-login-instructions-text">Sign in with your admin credentials to access the internal dashboard.</p>
        </div>
    );

    render() {
        const { view, error, loading } = this.state;

        if (view === "loading") {
            return (
                <TileableComponent instructions={this.instructions()}>
                    <div className="admin-login-container">
                        <button
                            type="button"
                            className="admin-login-back"
                            onClick={this.goBack}
                        >
                            ← Back
                        </button>
                        <p className="admin-login-loading">Loading…</p>
                    </div>
                </TileableComponent>
            );
        }

        return (
            <TileableComponent instructions={this.instructions()}>
                <div className="admin-login-container">
                    <button
                        type="button"
                        className="admin-login-back"
                        onClick={this.goBack}
                    >
                        ← Back
                    </button>
                    {view === "login" && (
                        <>
                            <h3>Admin Login</h3>
                            <AdminLoginForm
                                onSubmit={this.handleLoginSubmit}
                                error={error || undefined}
                                loading={loading}
                            />
                        </>
                    )}
                    {view === "2fa" && (
                        <>
                            <h3>Two-factor verification</h3>
                            <Admin2FAForm
                                onSubmit={this.handle2FASubmit}
                                onResend={this.handleResend2fa}
                                resendCooldownSeconds={this.state.resendCooldownSeconds}
                                resendLoading={this.state.resendLoading}
                                error={error || undefined}
                                loading={loading}
                            />
                        </>
                    )}
                </div>
            </TileableComponent>
        );
    }
}
